import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { BookingsSectionTitle } from "../styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { getOptionsFromRefList, getRefListNameByID } from "@/util/common_function";
import notificationService from "@/util/notificationService";
import { useSelector } from "react-redux";
import { SCHEMA_NAME } from "@/util/common_variable";

const BookingQueueDetailForm = forwardRef(
  (
    {
      stepData,
      bookings,
      existingSteps,
      isEditing,
      onCloseModal,
      queueType = "bookingQueue",
      startingPoint = "",
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const { bookingsFieldsEnabled, manualBookingData } = useSelector(
      (state) => state.bookings
    );

    // -----------------------------------------------------------------------
    // Derived constants from queueType
    // -----------------------------------------------------------------------
    const isReleaseQueue = queueType === "releaseQueue";
    const stepKey        = isReleaseQueue ? "releaseQueStepID"    : "queStepID";
    const detailIDKey    = isReleaseQueue ? "releaseQueueDetailID" : "bookingQueueDetailID";
    const stepRefList    = isReleaseQueue ? "ReleaseQueueStep"     : "BookingQueueStep";

    const [stepOptions, setStepOptions] = useState(
      getOptionsFromRefList(stepRefList, false, false, true)
    );

    const [queueDetail, setQueueDetail] = useState({
      [detailIDKey]:      stepData?.[detailIDKey] || null,
      [stepKey]:          stepData?.value || "",
      completionDateTime: stepData?.value && !stepData?.[detailIDKey] ? dayjs() : "",
      completionStaffID:  stepData?.value ? userDetails?.staffId : "",
      comments:           "",
      complete:           stepData?.value ? true : false,
      dateEnteredAudit:   stepData?.dateAdded,
      timeEnteredAudit:   stepData?.timeAdded,
      idAudit:            detailIDKey === "bookingQueueDetailID"
        ? stepData?.bookingQueueDetailID
        : stepData?.releaseQueueDetailID,
      creatorStaff: stepData?.creatorStaffID
        ? getRefListNameByID("", stepData.creatorStaffID, true)
        : "",
    });

    // -----------------------------------------------------------------------
    // Manual booking — filter step options to matching step only
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (manualBookingData?.isManualBooking) {
        const list = getOptionsFromRefList(stepRefList, false, false, true);
        const manualStep = list?.find((ms) =>
          ms.label.includes(manualBookingData?.value)
        );
        setStepOptions(manualStep ? [manualStep] : []);
      }
    }, []);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      getFieldsValue: async () => {
        const formValues = await form.validateFields();
        const { completionDateTime } = formValues;

        // Duplicate step check
        const isDuplicate = isEditing
          ? existingSteps?.some(
              (id) => id === formValues[stepKey] && id !== stepData[stepKey]
            )
          : existingSteps?.some((id) => id === formValues[stepKey]);

        if (isDuplicate) {
          notificationService.warning(
            "This step is already added. Please select a different step."
          );
          onCloseModal();
          throw new Error("Duplicate StepID");
        }

        const formattedValues = {
          ...formValues,
          completionDate: completionDateTime
            ? dayjs(completionDateTime).format("MM/DD/YYYY")
            : null,
          completionTime: completionDateTime
            ? dayjs(completionDateTime).format("HH:mm")
            : null,
        };
        delete formattedValues.completionDateTime;
        return formattedValues;
      },
      resetFields: () => form.resetFields(),
    }));

    // -----------------------------------------------------------------------
    // Sync stepData → local state
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (stepData) {
        setQueueDetail({
          ...stepData,
          completionDateTime: stepData.completionDate
            ? dayjs(`${stepData.completionDate} ${stepData.completionTime || ""}`)
            : !stepData?.[detailIDKey]
            ? dayjs()
            : null,
        });
      }
    }, [stepData]);

    // Sync local state → antd form
    useEffect(() => {
      form.setFieldsValue(queueDetail);
    }, [queueDetail, form]);

    // -----------------------------------------------------------------------
    // Complete checkbox — auto-fills or clears completion fields
    // -----------------------------------------------------------------------
    const handleCompleteChange = (e) => {
      if (e.target.checked) {
        form.setFieldsValue({
          completionDateTime: dayjs(),
          completionStaffID:  userDetails?.staffId,
        });
      } else {
        form.setFieldsValue({ completionDateTime: "", completionStaffID: "" });
      }
    };

    const handleDateChange = (value) => {
      form.setFieldsValue({ completionDateTime: value });
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <FieldSecurityProvider
        entity={isReleaseQueue ? SCHEMA_NAME.RELEASE_QUEUE_DETAILS : SCHEMA_NAME.BOOKING_QUEUE_DETAILS}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form form={form} layout="vertical">

          {/* Hidden ID field — no security needed */}
          <Form.Item name={detailIDKey} hidden>
            <input type="hidden" />
          </Form.Item>

          <StyledGridRow gutter={16}>
            <StyledGridCol xs={6}>
              {/* Complete — no overrideDisabled, controlled only by security */}
              <SecureFormItem
                fieldName="complete"
                formItemProps={{
                  name: "complete", label: "Complete",
                  layout: "horizontal", labelPostion: "label-right",
                  valuePropName: "checked",
                }}
              >
                <Checkbox onChange={handleCompleteChange} />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow gutter={16}>

            {/* Step — disabled when editing (business rule) */}
            <StyledGridCol xs={6}>
              <SecureFormItem
                fieldName={stepKey}
                overrideDisabled={isEditing}
                formItemProps={{
                  name: stepKey, label: "Step",
                  rules: [{ required: true, message: "Please select a step" }],
                }}
              >
                <Select
                  options={stepOptions}
                  placeholder="Step"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Completion Date/Time — disabled when editing */}
            <StyledGridCol xs={6}>
              <SecureFormItem
                fieldName="completionDateTime"
                overrideDisabled={isEditing}
                formItemProps={{ name: "completionDateTime", label: "Completion Date/Time" }}
              >
                <CommonDatePicker
                  placeholder="MM/DD/YYYY HH:mm"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: "HH:mm" }}
                  onChange={handleDateChange}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Completed By — disabled when editing */}
            <StyledGridCol xs={12}>
              <SecureFormItem
                fieldName="completionStaffID"
                overrideDisabled={isEditing}
                formItemProps={{ name: "completionStaffID", label: "Completed By" }}
              >
                <Select
                  options={getOptionsFromRefList("", true, false, true)}
                  placeholder="Completed By"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Comments — disabled when editing */}
            <StyledGridCol xs={24}>
              <SecureFormItem
                fieldName="comments"
                overrideDisabled={isEditing}
                formItemProps={{ name: "comments", label: "Comments" }}
              >
                <TextField inputType="textarea" />
              </SecureFormItem>
            </StyledGridCol>

          </StyledGridRow>

          {/* ── Administrative Information (audit fields — always read-only) ── */}
          <BookingsSectionTitle>
            <span>
              {isReleaseQueue
                ? "Release Queue Details Administrative Information"
                : "Booking Queue Details Administrative Information"}
            </span>
          </BookingsSectionTitle>

          <StyledGridRow gutter={16}>
            <StyledGridCol xs={4}>
              <SecureFormItem
                fieldName="idAudit"
                overrideDisabled
                formItemProps={{ name: "idAudit", label: "ID" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol xs={4}>
              <SecureFormItem
                fieldName="dateEnteredAudit"
                overrideDisabled
                formItemProps={{ name: "dateEnteredAudit", label: "Date Entered" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol xs={4}>
              <SecureFormItem
                fieldName="timeEnteredAudit"
                overrideDisabled
                formItemProps={{ name: "timeEnteredAudit", label: "Time Entered" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol xs={6}>
              <SecureFormItem
                fieldName="creatorStaff"
                overrideDisabled
                formItemProps={{ name: "creatorStaff", label: "User" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

        </Form>
      </FieldSecurityProvider>
    );
  }
);

export default BookingQueueDetailForm;